import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Academy } from '@court-sync/shared';
import axios from 'axios';
import { AlertTriangle, Link2, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  disconnectAccountingIntegration,
  fetchAccountingIntegration,
  fetchAccountingPostingCatalog,
  patchAccountingPostingDefaults,
  startAccountingOAuth,
  type AccountingIntegrationStatusResponse,
  type AccountingInvoicePostingMode,
  type AccountingPaymentSyncMode,
  type AccountingPostingCatalogAccount,
  type AccountingPostingCatalogTaxRate,
  type AccountingProvider,
} from '../../../../../services/accountingIntegration';
import { AccountingConnectionStatus } from './AccountingConnectionStatus';
import { AccountingDisconnectDialog } from './AccountingDisconnectDialog';
import { AccountingOAuthCallbackHandler } from './AccountingOAuthCallbackHandler';
import { AccountingPostingDefaults } from './AccountingPostingDefaults';
import { AccountingProviderPicker } from './AccountingProviderPicker';
import { accountingCatalogRefreshStorageKey } from './accountingIntegrationUi';
import { buildSupportUrl } from '@/utils/supportUrl';

interface Props {
  academy: Academy;
}

function applyStatusToForm(
  data: AccountingIntegrationStatusResponse,
  setters: {
    setPaymentAccountId: (value: string) => void;
    setSalesAccountId: (value: string) => void;
    setTaxCodeId: (value: string) => void;
    setInvoicePostingMode: (value: AccountingInvoicePostingMode) => void;
    setPaymentSyncMode: (value: AccountingPaymentSyncMode) => void;
  }
) {
  setters.setPaymentAccountId(data.defaultPaymentAccountId ?? '');
  setters.setSalesAccountId(data.defaultSalesAccountId ?? '');
  setters.setTaxCodeId(data.defaultTaxCodeId ?? '');
  setters.setInvoicePostingMode(data.invoicePostingMode ?? 'authorised');
  setters.setPaymentSyncMode(data.paymentSyncMode ?? 'immediate');
}

function isAccountingConnected(status: AccountingIntegrationStatusResponse | null): boolean {
  return !!(
    status &&
    status.status !== 'disconnected' &&
    (status.connectedAt != null || status.platform != null || status.provider != null)
  );
}

export function AccountingIntegrationsContent({ academy }: Props) {
  const [status, setStatus] = useState<AccountingIntegrationStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [connectingProvider, setConnectingProvider] = useState<AccountingProvider | null>(null);
  const [busy, setBusy] = useState(false);
  const [catalogAccounts, setCatalogAccounts] = useState<AccountingPostingCatalogAccount[] | null>(
    null
  );
  const [catalogTaxRates, setCatalogTaxRates] = useState<AccountingPostingCatalogTaxRate[] | null>(
    null
  );
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [postingSaveBusy, setPostingSaveBusy] = useState(false);
  const [paymentAccountId, setPaymentAccountId] = useState('');
  const [salesAccountId, setSalesAccountId] = useState('');
  const [taxCodeId, setTaxCodeId] = useState('');
  const [invoicePostingMode, setInvoicePostingMode] =
    useState<AccountingInvoicePostingMode>('authorised');
  const [paymentSyncMode, setPaymentSyncMode] = useState<AccountingPaymentSyncMode>('immediate');
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [lastCatalogRefreshAt, setLastCatalogRefreshAt] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchAccountingIntegration(academy.id);
      setStatus(data);
      applyStatusToForm(data, {
        setPaymentAccountId,
        setSalesAccountId,
        setTaxCodeId,
        setInvoicePostingMode,
        setPaymentSyncMode,
      });
    } catch (error) {
      console.error(error);
      toast.error('Could not load accounting integration status');
    } finally {
      setLoading(false);
    }
  }, [academy.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const connected = isAccountingConnected(status);

  useEffect(() => {
    let cancelled = false;
    if (!connected) {
      setCatalogAccounts(null);
      setCatalogTaxRates(null);
      return undefined;
    }

    (async () => {
      try {
        setCatalogLoading(true);
        const catalog = await fetchAccountingPostingCatalog(academy.id);
        if (!cancelled) {
          setCatalogAccounts(catalog.accounts);
          setCatalogTaxRates(catalog.taxRates);
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setCatalogAccounts(null);
          setCatalogTaxRates(null);
          toast.error('Could not load your accounts from the accounting software');
        }
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [academy.id, connected]);

  useEffect(() => {
    if (!connected) {
      setLastCatalogRefreshAt(null);
      return;
    }
    try {
      const raw = localStorage.getItem(accountingCatalogRefreshStorageKey(academy.id));
      if (raw && !Number.isNaN(Date.parse(raw))) {
        setLastCatalogRefreshAt(raw);
      }
    } catch {
      /* ignore */
    }
  }, [academy.id, connected]);

  const handleConnect = useCallback(
    async (provider: AccountingProvider) => {
      try {
        setConnectingProvider(provider);
        setBusy(true);
        const { authorizationUrl } = await startAccountingOAuth(academy.id, provider);
        if (!authorizationUrl?.trim()) {
          toast.error('Could not start connection — no authorization URL returned');
          setConnectingProvider(null);
          return;
        }
        window.location.assign(authorizationUrl);
      } catch (error: unknown) {
        const msg = axios.isAxiosError(error)
          ? (error.response?.data as { error?: string } | undefined)?.error
          : undefined;
        toast.error(msg || 'Could not start connection');
        setConnectingProvider(null);
      } finally {
        setBusy(false);
      }
    },
    [academy.id]
  );

  const handleSavePostingDefaults = async () => {
    try {
      setPostingSaveBusy(true);
      await patchAccountingPostingDefaults(academy.id, {
        defaultPaymentAccountId: paymentAccountId || null,
        defaultSalesAccountId: salesAccountId || null,
        defaultTaxCodeId: taxCodeId || null,
        invoicePostingMode,
        paymentSyncMode,
      });
      toast.success('Accounting settings saved');
      await load();
    } catch (error: unknown) {
      const msg = axios.isAxiosError(error)
        ? (error.response?.data as { error?: string } | undefined)?.error
        : undefined;
      toast.error(msg || 'Could not save accounting settings');
    } finally {
      setPostingSaveBusy(false);
    }
  };

  const handleConfirmDisconnect = async () => {
    try {
      setBusy(true);
      const result = await disconnectAccountingIntegration(academy.id);
      toast.success('Accounting software disconnected');
      if (result.message) {
        toast.error(result.message, { duration: 14_000 });
      }
      setDisconnectOpen(false);
      try {
        localStorage.removeItem(accountingCatalogRefreshStorageKey(academy.id));
      } catch {
        /* ignore */
      }
      setLastCatalogRefreshAt(null);
      await load();
    } catch {
      toast.error('Could not disconnect');
    } finally {
      setBusy(false);
    }
  };

  const handleRefreshFromAccounting = async () => {
    if (!connected) return;
    setRefreshBusy(true);
    setCatalogLoading(true);
    try {
      await toast.promise(
        Promise.all([
          fetchAccountingIntegration(academy.id),
          fetchAccountingPostingCatalog(academy.id),
        ]).then(([integrationData, catalog]) => {
          setStatus(integrationData);
          applyStatusToForm(integrationData, {
            setPaymentAccountId,
            setSalesAccountId,
            setTaxCodeId,
            setInvoicePostingMode,
            setPaymentSyncMode,
          });
          setCatalogAccounts(catalog.accounts);
          setCatalogTaxRates(catalog.taxRates);
          const refreshedAt = new Date();
          const iso = refreshedAt.toISOString();
          setLastCatalogRefreshAt(iso);
          try {
            localStorage.setItem(accountingCatalogRefreshStorageKey(academy.id), iso);
          } catch {
            /* ignore */
          }
          return refreshedAt;
        }),
        {
          loading:
            'Refreshing accounts and tax rates from your accounting software. This may take up to a minute for large organizations—please keep this page open.',
          success: (refreshedAt) =>
            `Connection status, accounts, and tax rates are up to date (${refreshedAt.toLocaleString()}).`,
          error: 'Could not refresh. Try again in a moment.',
        }
      );
    } catch (error) {
      console.error(error);
    } finally {
      setRefreshBusy(false);
      setCatalogLoading(false);
    }
  };

  return (
    <>
      <AccountingOAuthCallbackHandler onHandled={() => void load()} />
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Link2 className="h-5 w-5" aria-hidden />
            Accounting integration
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Connect Xero or QuickBooks Online to automatically sync lesson payments, refunds, payouts,
            and customer records to your accounting software.
          </p>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            Need help connecting or troubleshooting your accounting integration?{' '}
            <Link
              to={buildSupportUrl({
                type: 'technical',
                subject: 'Accounting integration help',
                message:
                  'I need help with the accounting integration (Xero / QuickBooks Online).',
                academyId: academy.id,
              })}
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              Contact support
            </Link>
            .
          </p>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            Loading…
          </div>
        ) : (
          <>
            {status?.lastError?.message ? (
              <div className="flex gap-2 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-900 dark:text-red-100">
                <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden />
                <div>
                  <p className="font-medium">Sync error</p>
                  <p>{status.lastError.message}</p>
                  <p className="text-xs mt-1 opacity-80">
                    Try disconnecting and reconnecting your accounting software.
                  </p>
                </div>
              </div>
            ) : null}

            {connected && status ? (
              <>
                <AccountingConnectionStatus
                  academyId={academy.id}
                  status={status}
                  lastCatalogRefreshAt={lastCatalogRefreshAt}
                  busy={busy}
                  refreshBusy={refreshBusy}
                  onReconnect={handleConnect}
                  onRefresh={() => void handleRefreshFromAccounting()}
                  onDisconnect={() => setDisconnectOpen(true)}
                />
                <AccountingPostingDefaults
                  catalogAccounts={catalogAccounts}
                  catalogTaxRates={catalogTaxRates}
                  catalogLoading={catalogLoading}
                  paymentAccountId={paymentAccountId}
                  salesAccountId={salesAccountId}
                  taxCodeId={taxCodeId}
                  invoicePostingMode={invoicePostingMode}
                  paymentSyncMode={paymentSyncMode}
                  saveBusy={postingSaveBusy}
                  onPaymentAccountIdChange={setPaymentAccountId}
                  onSalesAccountIdChange={setSalesAccountId}
                  onTaxCodeIdChange={setTaxCodeId}
                  onInvoicePostingModeChange={setInvoicePostingMode}
                  onPaymentSyncModeChange={setPaymentSyncMode}
                  onSave={() => void handleSavePostingDefaults()}
                />
                <AccountingDisconnectDialog
                  open={disconnectOpen}
                  onOpenChange={setDisconnectOpen}
                  busy={busy}
                  onConfirm={() => void handleConfirmDisconnect()}
                />
              </>
            ) : (
              <AccountingProviderPicker
                connectingProvider={connectingProvider}
                onConnect={handleConnect}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
